/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import LinearProgress from "@mui/material/LinearProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { get, post } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosCard, IosFilterBar, IosList, IosListItem, IosPage, IosSectionHeader, IosWorkflowCard, iosColors } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

const formatMoney = (value) => {
  const n = value == null ? 0 : Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
};

const primaryActionSx = {
  bgcolor: "#B54708",
  color: "#fff",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#953A05", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "rgba(181,71,8,0.18)", color: "rgba(15,15,18,0.44)" },
};

const successActionSx = {
  bgcolor: iosColors.green,
  color: "#fff",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#0D6149", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "rgba(20,122,92,0.18)", color: "rgba(15,15,18,0.44)" },
};

const secondaryActionSx = {
  bgcolor: "rgba(255,106,0,0.07)",
  color: "#8F3700",
  borderColor: "rgba(181,71,8,0.38)",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  "&:hover": { bgcolor: "rgba(255,106,0,0.12)", borderColor: "rgba(181,71,8,0.52)" },
};

const STATUS_OPTIONS = [
  { key: "all", label: "All", icon: "list_alt", accent: "orange" },
  { key: "owed", label: "Owed", icon: "assignment_late", accent: "orange" },
  { key: "label_generated", label: "Label", icon: "local_shipping", accent: "green" },
  { key: "in_transit", label: "Transit", icon: "sync", accent: "green" },
  { key: "received_pending_approval", label: "Inspect", icon: "fact_check", accent: "orange" },
  { key: "approved", label: "Applied", icon: "check_circle", accent: "green" },
  { key: "forfeited", label: "Forfeit", icon: "block", accent: "red" },
];

const pretty = (value, fallback = "-") => {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text.replaceAll("_", " ").replace(/\b\w/g, (m) => m.toUpperCase());
};

const getQueueRowKey = (row) => row?.documentId || `${row?.invoiceDocumentId || "inv"}:${row?.salesOrderItemDocumentId || "item"}`;

const dateTimeText = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

export default function CoreReturnsMobile() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [queueSearch, setQueueSearch] = useState("");
  const [submittedQueueSearch, setSubmittedQueueSearch] = useState("");
  const [queueStatus, setQueueStatus] = useState("all");
  const [selectedQueueKey, setSelectedQueueKey] = useState("");
  const [exceptionNotes, setExceptionNotes] = useState("");
  const [dropOffNotes, setDropOffNotes] = useState("");
  const [notice, setNotice] = useState(null);

  const [search, setSearch] = useState("");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  const [returnOpen, setReturnOpen] = useState(false);
  const [returnItem, setReturnItem] = useState(null);
  const [soldUnitId, setSoldUnitId] = useState("");
  const [coreSerialNumber, setCoreSerialNumber] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    const t = window.setTimeout(() => setSubmittedQueueSearch(queueSearch.trim()), 250);
    return () => window.clearTimeout(t);
  }, [queueSearch]);

  const queueQuery = useQuery({
    queryKey: ["m-core-returns-queue", submittedQueueSearch, queueStatus],
    queryFn: () =>
      get("/api/core-returns/staff/queue", {
        search: submittedQueueSearch || undefined,
        status: queueStatus,
        page: 1,
        pageSize: 50,
      }),
    staleTime: 5_000,
  });

  const queueRows = useMemo(() => (Array.isArray(queueQuery.data?.data) ? queueQuery.data.data : []), [queueQuery.data]);
  const queueSummary = queueQuery.data?.meta?.summary || {};
  const selectedQueueRow = useMemo(() => {
    if (!selectedQueueKey) return null;
    return queueRows.find((row) => getQueueRowKey(row) === selectedQueueKey) || null;
  }, [queueRows, selectedQueueKey]);

  useEffect(() => {
    if (!queueRows.length) {
      setSelectedQueueKey("");
      return;
    }
    if (!selectedQueueKey || !queueRows.some((row) => getQueueRowKey(row) === selectedQueueKey)) {
      setSelectedQueueKey(getQueueRowKey(queueRows[0]));
    }
  }, [queueRows, selectedQueueKey]);

  useEffect(() => {
    setExceptionNotes("");
    setDropOffNotes("");
    setNotice(null);
  }, [selectedQueueKey]);

  const approveMutation = useMutation({
    mutationFn: async () => {
      const row = selectedQueueRow;
      if (!row?.invoiceDocumentId || !row?.salesOrderItemDocumentId) throw new Error("Missing invoice or line item reference.");
      const existingInspectionCount = Number(row?.returnRequest?.warehouseInspectionImageCount || 0);
      if (!(existingInspectionCount > 0) || !row?.returnRequest?.warehouseInspectedAt) {
        throw new Error("Warehouse must receive this core (photos) before credit can be applied.");
      }
      return post("/api/core-returns/approve", {
        data: {
          invoice: row.invoiceDocumentId,
          salesOrderItem: row.salesOrderItemDocumentId,
          soldUnit: row?.unit?.documentId || null,
          returnRequestDocumentId: row?.returnRequest?.documentId || null,
          warehouseInspectionImageIds: [],
          notes: exceptionNotes.trim() || "Approved after warehouse physical receive.",
        },
      });
    },
    onSuccess: async () => {
      setNotice({ type: "success", text: "Core credit applied." });
      setExceptionNotes("");
      await qc.invalidateQueries({ queryKey: ["m-core-returns-queue"] });
      await qc.invalidateQueries({ queryKey: ["core-returns-queue"] });
    },
    onError: (error) => setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Unable to approve core return." }),
  });

  const dropOffMutation = useMutation({
    mutationFn: async () => {
      const row = selectedQueueRow;
      const soldUnit = row?.unit?.documentId || row?.units?.[0]?.documentId || null;
      if (!soldUnit) throw new Error("Select a core return with a linked sold unit.");
      if (!row?.customer?.documentId) throw new Error("Customer is required to record drop-off.");
      return post("/api/storefront/staff/core-returns/drop-off", {
        data: {
          soldUnit,
          customerDocumentId: row.customer.documentId,
          invoiceDocumentId: row.invoiceDocumentId || null,
          salesOrderItemDocumentId: row.salesOrderItemDocumentId || null,
          notes: dropOffNotes.trim() || null,
        },
      });
    },
    onSuccess: async () => {
      setNotice({ type: "success", text: "Drop-off recorded. Open Warehouse Receiving to intake the core." });
      setDropOffNotes("");
      await qc.invalidateQueries({ queryKey: ["m-core-returns-queue"] });
      await qc.invalidateQueries({ queryKey: ["core-returns-queue"] });
    },
    onError: (error) =>
      setNotice({
        type: "error",
        text: error?.response?.data?.error?.message || error?.message || "Unable to record in-person drop-off.",
      }),
  });

  const invoicesQuery = useQuery({
    queryKey: ["m-core-returns-invoices", search],
    queryFn: () =>
      get("/api/sales-orders", {
        sort: "updatedAt:desc",
        pagination: { page: 1, pageSize: 25 },
        filters: {
          documentType: { $eq: "invoice" },
          ...(search && { salesOrderNumber: { $containsi: search } }),
        },
      }),
    staleTime: 5_000,
  });

  const invoiceRows = useMemo(() => (invoicesQuery.data?.data || []).map(normalize), [invoicesQuery.data]);

  const invoiceQuery = useQuery({
    queryKey: ["m-core-returns-invoice", selectedInvoiceId],
    queryFn: () =>
      get(`/api/sales-orders/${selectedInvoiceId}`, {
        populate: {
          customer: true,
          items: { populate: { inventoryItem: true, units: true } },
        },
      }),
    enabled: !!selectedInvoiceId,
    staleTime: 2_000,
  });

  const invoice = useMemo(() => (invoiceQuery.data?.data ? normalize(invoiceQuery.data.data) : null), [invoiceQuery.data]);
  const items = useMemo(() => {
    const raw = invoice?.items?.data || invoice?.items || [];
    return raw.map(normalize);
  }, [invoice]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + (Number(it.lineTotal || 0) || 0), 0);
    const coreOutstanding = items.reduce((s, it) => {
      const qty = Number(it.quantity || 0) || 0;
      const returned = Number(it.coreReturnedQty || 0) || 0;
      const outstandingQty = Math.max(0, qty - returned);
      const per = it.coreCharge != null ? Number(it.coreCharge) || 0 : 0;
      return s + outstandingQty * per;
    }, 0);
    return { subtotal, coreOutstanding };
  }, [items]);

  const processMutation = useMutation({
    mutationFn: async () => {
      if (!invoice) throw new Error("Select an invoice");
      if (!returnItem) throw new Error("Select a line item");
      return post("/api/core-returns/process", {
        data: {
          invoice: invoice.documentId || invoice.id,
          salesOrderItem: returnItem.documentId || returnItem.id,
          soldUnit: soldUnitId || null,
          coreSerialNumber: coreSerialNumber.trim() || null,
          notes: notes.trim() || null,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-core-returns-invoice", selectedInvoiceId] });
      qc.invalidateQueries({ queryKey: ["m-core-returns-queue"] });
      setReturnOpen(false);
      setReturnItem(null);
      setSoldUnitId("");
      setCoreSerialNumber("");
      setNotes("");
      window.alert("Core return recorded and core charge updated.");
    },
    onError: (err) => {
      window.alert(err?.message || "Failed to process core return.");
    },
  });

  return (
    <IosPage>
      <MobileTopBar title="Core Returns" subtitle={queueQuery.isFetching ? "Syncing..." : "Recovery workflow"} onBack={() => navigate("/m/sales")} />

      <Stack spacing={1.05} sx={{ mt: 1 }}>
        <IosSectionHeader
          eyebrow="Core recovery"
          title="Get cores back"
          subtitle="Shipping assist + credit/forfeit. Physical receive is in Warehouse Receiving → Incoming cores."
        />

        <IosFilterBar label="Stage" value={queueStatus} options={STATUS_OPTIONS} onChange={setQueueStatus} />
        <TextField
          fullWidth
          placeholder="Search invoice, customer, tag, tracking"
          value={queueSearch}
          onChange={(e) => setQueueSearch(e.target.value)}
        />
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <MetricChip label={`Owed ${queueSummary.owed || 0}`} />
          <MetricChip label={`Label ${queueSummary.label_generated || 0}`} tone="green" />
          <MetricChip label={`Transit ${queueSummary.in_transit || 0}`} tone="green" />
          <MetricChip label={`Received ${queueSummary.received_pending_approval || 0}`} />
          <MetricChip label={`Forfeit ${queueSummary.forfeited || 0}`} tone="red" />
        </Stack>
        {queueQuery.isLoading ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
        {notice ? (
          <IosCard sx={{ borderColor: notice.type === "success" ? "rgba(20,122,92,0.35)" : "rgba(215,38,30,0.35)" }}>
            <Typography sx={{ color: notice.type === "success" ? iosColors.green : iosColors.red, fontSize: 13, fontWeight: 850 }}>
              {notice.text}
            </Typography>
          </IosCard>
        ) : null}

        <IosList>
          {queueRows.map((row) => {
            const rowKey = getQueueRowKey(row);
            const selected = rowKey === selectedQueueKey;
            return (
              <IosListItem
                key={rowKey}
                icon={selected ? "check_circle" : "assignment_return"}
                title={row.invoiceNumber || row.invoiceDocumentId || "Core return"}
                meta={[
                  row?.customer?.name || "No customer",
                  row?.unit?.optionalTag || row?.unit?.serialNumber || row?.unit?.documentId || "No unit",
                  row?.returnRequest?.trackingNumber || "No tracking",
                ].join(" • ")}
                trailing={pretty(row.lifecycleStatus)}
                accent={row.lifecycleStatus === "approved" ? "green" : row.lifecycleStatus === "forfeited" ? "red" : "orange"}
                onClick={() => setSelectedQueueKey(rowKey)}
              />
            );
          })}
          {!queueRows.length && !queueQuery.isLoading ? (
            <Typography sx={{ color: iosColors.muted, fontSize: 13, px: 1.2, py: 1.4 }}>No core returns match this view.</Typography>
          ) : null}
        </IosList>

        {selectedQueueRow ? (
          <IosCard>
            <Stack spacing={0.85}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <MetricChip label={pretty(selectedQueueRow.lifecycleStatus)} />
                <MetricChip label={`${Number(selectedQueueRow.outstandingQty || 0)} due`} tone="orange" />
                <MetricChip label={formatMoney(selectedQueueRow.outstandingAmount)} tone="green" />
              </Stack>
              <InfoRow label="Customer" value={selectedQueueRow?.customer?.name || "-"} />
              <InfoRow label="Invoice" value={selectedQueueRow?.invoiceNumber || selectedQueueRow?.invoiceDocumentId || "-"} />
              <InfoRow label="Item" value={selectedQueueRow?.item?.sku || selectedQueueRow?.item?.name || "-"} />
              <InfoRow label="Unit" value={selectedQueueRow?.unit?.optionalTag || selectedQueueRow?.unit?.serialNumber || selectedQueueRow?.unit?.documentId || "-"} />
              <InfoRow
                label="Shipping"
                value={
                  selectedQueueRow?.returnRequest?.obligationTracker
                    ? selectedQueueRow?.coreDueAt || selectedQueueRow?.returnRequest?.coreDueAt
                      ? `Owed · due ${dateTimeText(selectedQueueRow.coreDueAt || selectedQueueRow.returnRequest.coreDueAt)}`
                      : "Owed · awaiting return start"
                    : selectedQueueRow?.returnRequest?.inPersonDropOff
                      ? selectedQueueRow?.returnRequest?.dropOffRecordedAt
                        ? `In-person drop-off · ${dateTimeText(selectedQueueRow.returnRequest.dropOffRecordedAt)}`
                        : "In-person drop-off · ready for warehouse"
                      : [selectedQueueRow?.returnRequest?.shippingCarrier, selectedQueueRow?.returnRequest?.trackingNumber]
                          .filter(Boolean)
                          .join(" • ") || "Needs shipping assist / label"
                }
              />
              <InfoRow
                label="Warehouse"
                value={
                  selectedQueueRow?.returnRequest?.warehouseInspectedAt
                    ? `Received ${dateTimeText(selectedQueueRow.returnRequest.warehouseInspectedAt)} · ${Number(selectedQueueRow?.returnRequest?.warehouseInspectionImageCount || 0)} photo(s)`
                    : "Not received yet"
                }
              />
              <Stack direction="row" spacing={0.75}>
                <Button
                  fullWidth
                  sx={primaryActionSx}
                  disabled={
                    !selectedQueueRow?.returnRequest?.documentId ||
                    selectedQueueRow?.returnRequest?.obligationTracker ||
                    selectedQueueRow?.returnRequest?.inPersonDropOff ||
                    !!(selectedQueueRow?.returnRequest?.shippingCarrier || selectedQueueRow?.returnRequest?.trackingNumber)
                  }
                  onClick={() =>
                    navigate(
                      `/m/warehouse/shipping?sourceId=${encodeURIComponent(selectedQueueRow.returnRequest.documentId)}&requestType=core_return`
                    )
                  }
                >
                  Shipping
                </Button>
                <Button
                  fullWidth
                  sx={secondaryActionSx}
                  disabled={!selectedQueueRow?.returnRequest?.documentId && !selectedQueueRow?.unit?.documentId}
                  onClick={() =>
                    navigate(
                      selectedQueueRow?.returnRequest?.documentId
                        ? `/m/warehouse/receiving/cr-${selectedQueueRow.returnRequest.documentId}`
                        : "/m/warehouse/receiving?tab=cores"
                    )
                  }
                >
                  Receive
                </Button>
              </Stack>
              {!selectedQueueRow?.returnRequest?.warehouseInspectedAt &&
              !selectedQueueRow?.returnRequest?.inPersonDropOff &&
              !(selectedQueueRow?.returnRequest?.shippingCarrier || selectedQueueRow?.returnRequest?.trackingNumber) ? (
                <>
                  <TextField
                    fullWidth
                    label="Drop-off notes"
                    multiline
                    minRows={2}
                    value={dropOffNotes}
                    onChange={(e) => setDropOffNotes(e.target.value)}
                    placeholder="Optional — counter visit, packaging notes"
                  />
                  {dropOffMutation.isPending ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
                  <Button
                    fullWidth
                    sx={primaryActionSx}
                    disabled={
                      dropOffMutation.isPending ||
                      !(selectedQueueRow?.unit?.documentId || selectedQueueRow?.units?.[0]?.documentId) ||
                      !selectedQueueRow?.customer?.documentId
                    }
                    onClick={() => dropOffMutation.mutate()}
                  >
                    Record in-person drop-off
                  </Button>
                </>
              ) : null}
              {selectedQueueRow?.returnRequest?.inPersonDropOff ? (
                <Button
                  fullWidth
                  sx={secondaryActionSx}
                  onClick={() =>
                    navigate(`/m/warehouse/receiving/cr-${selectedQueueRow.returnRequest.documentId}`)
                  }
                >
                  Open warehouse receive
                </Button>
              ) : null}
              <TextField
                fullWidth
                label="Approval notes"
                multiline
                minRows={2}
                value={exceptionNotes}
                onChange={(e) => setExceptionNotes(e.target.value)}
              />
              {approveMutation.isPending ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
              <Button
                fullWidth
                sx={successActionSx}
                disabled={
                  !(Number(selectedQueueRow?.outstandingQty || 0) > 0) ||
                  !selectedQueueRow?.returnRequest?.warehouseInspectedAt ||
                  approveMutation.isPending
                }
                onClick={() => approveMutation.mutate()}
              >
                Apply credit
              </Button>
            </Stack>
          </IosCard>
        ) : null}

        <IosCard>
          <Box>
            <Typography variant="subtitle2" fontWeight={900}>
              Manual invoice exceptions
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Use only when a return request is missing or accounting needs a direct invoice correction.
            </Typography>
            <TextField
              sx={{ mt: 1 }}
              fullWidth
              placeholder="Invoice number…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {invoicesQuery.isFetching ? <LinearProgress sx={{ mt: 1 }} /> : null}
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <Stack spacing={0.75}>
              {(invoiceRows || []).map((row) => {
                const rowId = row.documentId || row.id;
                const isSel = String(selectedInvoiceId || "") === String(rowId);
                return (
                  <IosWorkflowCard
                    key={rowId}
                    icon={isSel ? "check_circle" : "receipt_long"}
                    title={row.salesOrderNumber || "Invoice"}
                    subtitle={isSel ? "selected" : row.status || "invoice"}
                    detail={`${row.status || "—"} • Total ${formatMoney(row.total)}`}
                    chips={[isSel ? "Selected" : "Select", formatMoney(row.total)]}
                    accent={isSel ? "green" : "orange"}
                    onClick={() => setSelectedInvoiceId(rowId)}
                  />
                );
              })}
              {!invoiceRows.length ? (
                <Typography variant="caption" color="text.secondary">
                  No invoices found.
                </Typography>
              ) : null}
            </Stack>
          </Box>
        </IosCard>

        {selectedInvoiceId ? (
          <IosCard>
            <Box>
              <Typography variant="subtitle2" fontWeight={900}>
                Invoice Details
              </Typography>
              {invoiceQuery.isLoading ? <LinearProgress sx={{ mt: 1 }} /> : null}
              {invoice ? (
                <Typography variant="caption" color="text.secondary">
                  Subtotal {formatMoney(totals.subtotal)} • Core outstanding {formatMoney(totals.coreOutstanding)}
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ mt: 1.5 }}>
              <Stack spacing={0.75}>
                {items.map((it) => {
                  const inv = it?.inventoryItem?.data ? normalize(it.inventoryItem.data) : it?.inventoryItem ? normalize(it.inventoryItem) : null;
                  const qty = Number(it.quantity || 0) || 0;
                  const returned = Number(it.coreReturnedQty || 0) || 0;
                  const outstandingQty = Math.max(0, qty - returned);
                  const perCore = it.coreCharge != null ? Number(it.coreCharge) || 0 : 0;
                  const showReturn = perCore > 0 && outstandingQty > 0;
                  const units = Array.isArray(it.units) ? it.units : it.units?.data || [];
                  const unitRows = units.map(normalize);

                  return (
                    <IosCard key={it.documentId || it.id} sx={{ minHeight: 0, p: 1.35, boxShadow: "none" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.75}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={900}>
                              {inv?.sku || inv?.name || "Item"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Qty {qty} • Core {perCore ? formatMoney(perCore) : "—"} • Returned {returned} • Outstanding {outstandingQty}
                            </Typography>
                          </Box>
                          {showReturn ? <Chip size="small" color="warning" label="Core due" /> : <Chip size="small" variant="outlined" label="OK" />}
                        </Stack>

                        {unitRows.length ? (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                            Units: {unitRows.map((u) => u.serialNumber).filter(Boolean).slice(0, 4).join(", ")}
                            {unitRows.length > 4 ? "…" : ""}
                          </Typography>
                        ) : null}

                        {showReturn ? (
	                          <Button
	                            sx={{ mt: 1, ...primaryActionSx }}
	                            fullWidth
	                            onClick={() => {
                              setReturnItem({ ...it, _unitRows: unitRows });
                              setReturnOpen(true);
                            }}
                          >
                            Return core (remove 1 core charge)
                          </Button>
                        ) : null}
                    </IosCard>
                  );
                })}

                {!items.length ? (
                  <Typography variant="caption" color="text.secondary">
                    No items on this invoice.
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          </IosCard>
        ) : null}
      </Stack>

      {/* Return dialog */}
      <Dialog open={returnOpen} onClose={() => (processMutation.isPending ? null : setReturnOpen(false))} fullScreen>
        <IosPage>
          <MobileTopBar
            title="Return Core"
            subtitle={invoice?.salesOrderNumber || ""}
            onBack={() => (processMutation.isPending ? null : setReturnOpen(false))}
          />

          <Stack spacing={0.75} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              This records a core return (invoice ↔ sold unit ↔ core serial) and increments `coreReturnedQty` by 1 on the selected line.
            </Typography>

            {returnItem ? (
              <IosCard>
                  <Typography variant="subtitle2" fontWeight={900}>
                    Line item
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Item {returnItem.documentId || returnItem.id}
                  </Typography>
              </IosCard>
            ) : null}

            <FormControl fullWidth size="small">
              <InputLabel>Sold unit (optional)</InputLabel>
              <Select
                label="Sold unit (optional)"
                value={soldUnitId}
                onChange={(e) => setSoldUnitId(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {(returnItem?._unitRows || []).map((u) => (
                  <MenuItem key={u.documentId || u.id} value={u.documentId || u.id}>
                    {u.serialNumber || (u.documentId || u.id)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Returned core serial/tag (optional)"
              placeholder="Scan/enter core tag…"
              value={coreSerialNumber}
              onChange={(e) => setCoreSerialNumber(e.target.value)}
            />
            <TextField label="Notes (optional)" multiline minRows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />

            <Stack direction="row" spacing={0.75}>
              <Button fullWidth sx={secondaryActionSx} disabled={processMutation.isPending} onClick={() => setReturnOpen(false)}>
                Cancel
              </Button>
              <Button fullWidth sx={successActionSx} disabled={processMutation.isPending} onClick={() => processMutation.mutate()}>
                {processMutation.isPending ? "Processing…" : "Process return"}
              </Button>
            </Stack>

            {processMutation.isPending ? <LinearProgress /> : null}
          </Stack>
        </IosPage>
      </Dialog>
    </IosPage>
  );
}

function MetricChip({ label, tone = "orange" }) {
  const color = tone === "red" ? iosColors.red : tone === "green" ? iosColors.green : "#8F3700";
  const bg = tone === "red" ? "rgba(215,38,30,0.07)" : tone === "green" ? "rgba(20,122,92,0.08)" : "rgba(255,106,0,0.07)";
  return <Chip label={label} size="small" sx={{ bgcolor: bg, color, fontSize: 11.75, fontWeight: 850 }} />;
}

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
      <Typography sx={{ color: iosColors.muted, fontSize: 12.75, fontWeight: 850 }}>{label}</Typography>
      <Typography sx={{ color: iosColors.ink, fontSize: 12.75, fontWeight: 800, textAlign: "right", maxWidth: "62%" }}>
        {value || "-"}
      </Typography>
    </Stack>
  );
}
